import React from 'react';
import PropTypes from 'prop-types';
import { partial } from 'ramda';
import { FormattedMessage } from 'react-intl';

import GroupPassengerSelection from './GroupPassengerSelection';
import StandardExpandedSelection from './StandardExpandedSelection';
import StandardPassengerSelection from './StandardPassengerSelection';
import AssistedPassengerSelection from './AssistedPassengerSelection';
import { BookingTypesContainer, BookingTypeButton, BookingTypes, BookingTypesHeader } from './PassengerSelection.styles';
import { disabilityTypes, searchTypes } from '../BookingMagnet.constants';

const PassengerSelection = ({
  onChangePassengers,
  passengers,
  className,
  canHaveGroupBookings,
  canHaveStandardBookings,
  canHaveAccessibleTravelBookings,
  passengerRangeError,
  changeBookingType,
  bookingTypeSelected,
  expandPaxTypes,
  disabilityType,
  isEurostarAgent,
  shouldShowDogOnlyAssistance,
  containSNCBDBOrNSStation = false,
  isCallCenterAgent,
  containDBStation,
  infantsOnLapEnabled,
  maxInfants
}) => (
  <div className={className}>
    <BookingTypes>
      <BookingTypesHeader>
        <FormattedMessage id="bookingType.passengers" />
      </BookingTypesHeader>

      <BookingTypesContainer data-testid="booking-types-container" count={3}>
        {canHaveStandardBookings && (
          <BookingTypeButton
            type="button"
            onClick={() => changeBookingType(searchTypes.STANDARD)}
            className={bookingTypeSelected === searchTypes.STANDARD && 'selected'}
            selected={bookingTypeSelected === searchTypes.STANDARD}
            data-cy="booking-type-button-standard"
          >
            <FormattedMessage id="bookingType.regular" />
          </BookingTypeButton>
        )}
        {canHaveGroupBookings && (
          <BookingTypeButton
            type="button"
            onClick={() => changeBookingType(searchTypes.GROUP)}
            className={bookingTypeSelected === searchTypes.GROUP && 'selected'}
            selected={bookingTypeSelected === searchTypes.GROUP}
          >
            <FormattedMessage id="bookingType.group" />
          </BookingTypeButton>
        )}
        {canHaveAccessibleTravelBookings && (
          <BookingTypeButton
            type="button"
            onClick={() => changeBookingType(searchTypes.WITH_DISABILITY)}
            className={bookingTypeSelected === searchTypes.WITH_DISABILITY && 'selected'}
            selected={bookingTypeSelected === searchTypes.WITH_DISABILITY}
            data-cy="booking-type-button-disability"
          >
            <FormattedMessage id="bookingType.disability" />
          </BookingTypeButton>
        )}
      </BookingTypesContainer>
    </BookingTypes>
    {bookingTypeSelected === searchTypes.GROUP && (
      <GroupPassengerSelection passengers={passengers} onChangePassengers={onChangePassengers} passengerRangeError={passengerRangeError} />
    )}
    {bookingTypeSelected === searchTypes.STANDARD &&
      (expandPaxTypes ? (
        <StandardExpandedSelection
          passengers={passengers}
          onChangePassengers={onChangePassengers}
          passengerRangeError={passengerRangeError}
        />
      ) : (
        <StandardPassengerSelection
          passengers={passengers}
          onChangePassengers={onChangePassengers}
          passengerRangeError={passengerRangeError}
          containSNCBDBOrNSStation={containSNCBDBOrNSStation}
          infantsOnLapEnabled={infantsOnLapEnabled}
          maxInfants={maxInfants}
        />
      ))}
    {bookingTypeSelected === searchTypes.WITH_DISABILITY && (
      <AssistedPassengerSelection
        isEurostarAgent={isEurostarAgent}
        shouldShowDogOnlyAssistance={shouldShowDogOnlyAssistance}
        passengers={passengers}
        disabilityType={disabilityType}
        onChangePassengers={onChangePassengers}
        onChangeDisabilityType={partial(changeBookingType, [searchTypes.WITH_DISABILITY])}
        isCallCenterAgent={isCallCenterAgent}
        containSNCBDBOrNSStation={containSNCBDBOrNSStation}
        containDBStation={containDBStation}
      />
    )}
  </div>
);

PassengerSelection.propTypes = {
  isEurostarAgent: PropTypes.bool,
  shouldShowDogOnlyAssistance: PropTypes.bool,
  onChangePassengers: PropTypes.func,
  passengers: PropTypes.shape(),
  className: PropTypes.string,
  canHaveStandardBookings: PropTypes.bool,
  canHaveGroupBookings: PropTypes.bool,
  canHaveAccessibleTravelBookings: PropTypes.bool,
  passengerRangeError: PropTypes.string,
  changeBookingType: PropTypes.func,
  bookingTypeSelected: PropTypes.oneOf([searchTypes.GROUP, searchTypes.STANDARD, searchTypes.WITH_DISABILITY]),
  expandPaxTypes: PropTypes.bool,
  disabilityType: PropTypes.oneOf(Object.values(disabilityTypes)),
  containSNCBDBOrNSStation: PropTypes.bool,
  isCallCenterAgent: PropTypes.bool,
  containDBStation: PropTypes.bool,
  infantsOnLapEnabled: PropTypes.bool,
  maxInfants: PropTypes.number
};

export default PassengerSelection;
